import { Select as AntSelect, SelectProps as AntSelectProps } from 'antd';
import { ChangeEvent, FC, useMemo } from 'react';
import { ISelectProps } from './types';
import { buildSelectClassName, inputLabelClassName, selectPopupClassName } from '../formControlStyles';

type AntOption = NonNullable<AntSelectProps['options']>[number];

function getPrimitiveValue(value: ISelectProps['value']) {
  if (value && typeof value === 'object' && 'value' in value) {
    return value.value;
  }

  return value ?? undefined;
}

function getGroupedOptions(options: ISelectProps['options'], groupBy?: ISelectProps['groupBy']): AntOption[] {
  if (!groupBy) {
    return options.map(option => ({
      label: option.label,
      value: option.value as string | number,
    }));
  }

  return Object.entries(
    options.reduce<Record<string, ISelectProps['options']>>((accumulator, option) => {
      const groupName = groupBy(option);
      accumulator[groupName] = [...(accumulator[groupName] ?? []), option];
      return accumulator;
    }, {}),
  ).map(([groupLabel, groupOptions]) => ({
    label: groupLabel,
    options: groupOptions.map(option => ({
      label: option.label,
      value: option.value as string | number,
    })),
  }));
}

export const Select: FC<ISelectProps> = ({
  className,
  options,
  value,
  defaultValue,
  onChange,
  disableClearable = true,
  disabled,
  placeholder,
  filterOptions,
  groupBy,
  id,
  showSearch = false,
  allowClear,
  popupClassName,
  suffixIcon,
  status,
  label,
  ...props
}) => {
  const normalizedOptions = filterOptions ? filterOptions(options) : options;
  const selectOptions = useMemo(() => getGroupedOptions(normalizedOptions, groupBy), [normalizedOptions, groupBy]);
  const normalizedValue = getPrimitiveValue(value);
  const normalizedDefaultValue = getPrimitiveValue(defaultValue);
  const hasValue = normalizedValue !== undefined && normalizedValue !== null && String(normalizedValue).length > 0;
  const canClear = allowClear ?? !disableClearable;

  function handleChange(nextValue: string | number | boolean | null | undefined) {
    const selectedOption =
      nextValue === undefined || nextValue === null
        ? null
        : normalizedOptions.find(option => option.value === nextValue) ?? null;

    onChange?.(
      {
        target: { value: selectedOption?.label ?? '' },
      } as ChangeEvent<HTMLSelectElement>,
      selectedOption,
    );
  }

  return (
    <div className={['relative w-full', className].filter(Boolean).join(' ')}>
      {label ? (
        <label htmlFor={id} className={inputLabelClassName}>
          {label}
        </label>
      ) : null}
      <AntSelect
        id={id}
        className={buildSelectClassName({
          isDisabled: disabled,
          hasValue,
          hasError: status === 'error',
        })}
        options={selectOptions}
        value={normalizedValue === null || normalizedValue === '' ? undefined : normalizedValue}
        defaultValue={normalizedDefaultValue === null ? undefined : normalizedDefaultValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        allowClear={canClear}
        showSearch={showSearch}
        optionFilterProp={'label'}
        popupClassName={[selectPopupClassName, popupClassName].filter(Boolean).join(' ')}
        suffixIcon={suffixIcon}
        status={status}
        aria-label={props['aria-label']}
        data-testid={props['data-testid']}
      />
    </div>
  );
};
